import { type Sx, type Theme, useTheme } from '@dhl-ui/theme'
import { useSpacingToken } from '../../theme/hooks/use-spacing-token'
import { type ListItemProps } from './types'

type Props = Pick<
  ListItemProps,
  | 'alignItems'
  | 'background'
  | 'variant'
  | 'isSelected'
  | 'isButton'
  | 'isDisabled'
  | 'isRounded'
  | 'isVertical'
  | 'onClick'
  | 'sx'
  | 'to'
> &
  Pick<NonNullable<ListItemProps['slotProps']>, 'secondaryAction' | 'secondaryIcon' | 'line'> & {
    py: number
  }

export const useStyle = ({
  isButton,
  variant,
  isSelected,
  isDisabled,
  background,
  isRounded,
  isVertical,
  alignItems,
  secondaryAction,
  secondaryIcon,
  line,
  onClick,
  sx,
  to,
  py,
}: Props) => {
  const { misc } = useTheme()
  const spacingToken = useSpacingToken()
  const px = sx?.px || spacingToken
  const isSelectable = variant === 'selectable'

  const styles: Sx = {
    alignItems,
    py,
    pl: px,
    // reason: its readable no need for function
    // eslint-disable-next-line no-nested-ternary
    pr: secondaryAction ? 8 : secondaryIcon ? 5 : px,
    display: 'flex',
    justifyContent: 'left',
    position: 'relative',
    textAlign: 'left',
    width: '100%',

    flexDirection: isVertical ? 'column' : 'row',

    // we can place a ListItem inside another ListItem when we make it selectable for example
    '& .DhlListItem-item': {
      p: 0,
      backgroundColor: 'inherit',
    },

    '> .DhlListItem-text': {
      alignContent: alignItems,
    },

    '> .DhlListItem-text:not(:last-child)': {
      pr: 2,
    },

    '> .DhlListItem-text:has(+ .DhlListItem-line), &.DhlListItem--vertical > .DhlListItem-text:not(:last-child)': {
      pr: 0,
    },

    // for when ListComponent is a Link
    outline: 0,
    color: `text.${sx?.color ?? 'primary'}`,
    textDecoration: 'none',

    // line prop should not be used with a list item inside another list item (Select composition)
    ...(line && {
      flexWrap: 'wrap',
      '& > :is(.DhlListItem-illustration, .DhlListItem-icon) ~ .DhlListItem-text': {
        flexBasis: 0,
      },
    }),

    // our default background when not selected
    ...(!isSelected && { backgroundColor: background === 'dark' ? 'background.default' : 'background.paper' }),

    ...(isRounded ? { borderRadius: misc.borderRadius(2) } : {}),

    ...((!isButton && onClick) || isSelectable
      ? {
          cursor: 'pointer',
        }
      : {}),

    ...((isButton || onClick || to) && {
      ...misc.createHoverOrActiveState({
        backgroundColor: 'action.hover',
      }),

      ...misc.createFocusVisibleState({
        backgroundColor: 'action.focus',
        outline: misc.borderStyle('info.main', 3),
        outlineOffset: misc.borderSize(-3),
      }),

      '&:active': {
        outline: 0,
      },

      ...(isDisabled && {
        opacity: (theme: Theme) => theme.palette.action.disabledOpacity,
        pointerEvents: 'none',
      }),
    }),

    ...(isSelected && {
      bgcolor: isSelectable ? 'background.paper' : 'action.focus',

      outline: misc.borderStyle('pure.dark', 2),
      outlineOffset: `-${misc.borderSize(2)}`,

      ...misc.createHoverOrActiveState({
        bgcolor: isSelectable ? undefined : 'action.selected',
      }),
    }),

    // state: disabled
    ...(isDisabled && {
      opacity: 0.3,
    }),

    zIndex: sx?.zIndex,
    minHeight: sx?.minHeight,
  }

  return styles
}
